import deepmerge from 'deepmerge';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getAlertStatus,
  getVehicleAvailableDateTime,
  getIsInExclusivePreview,
  getUserAvailability
} from '~/selectors';
import type { DeepPartial } from '~/types';

const twoHours = 60 * 60 * 2;
const fortyEightHours = 60 * 60 * 48;

export type ContextType = {
  availableDateTime: number;
  shouldShowTimer: boolean;
  isInitialized: boolean;
  timeLeft: number;
  timeLeftAsObject: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  timeLeftAsShortString: string;
  timeLeftAsString: string;
  wasTimerShown: boolean;
};

type TimeLeftObjectType = Pick<
  ContextType,
  'timeLeft' | 'timeLeftAsObject' | 'timeLeftAsShortString' | 'timeLeftAsString'
>;

const defaultContext: ContextType = {
  availableDateTime: 0,
  shouldShowTimer: false,
  isInitialized: false,
  timeLeft: twoHours * 1000,
  timeLeftAsObject: {
    hours: 0,
    minutes: 0,
    seconds: 0
  },
  timeLeftAsShortString: '',
  timeLeftAsString: '',
  wasTimerShown: false
};

const UnlockTimerContext = createContext<ContextType>(defaultContext);

const padWithZero = (number: number) => (number < 10 ? '0' : '') + number;

const calculateTimeLeft =
  (
    availableDateTime: number,
    setTimeLeftState: (_: TimeLeftObjectType) => void
  ) =>
  () => {
    const dateNow = Date.now();
    const timeLeft = availableDateTime
      ? availableDateTime - dateNow
      : defaultContext.timeLeft;

    const timeLeftAsObject =
      timeLeft > 0
        ? {
            hours: Math.floor(timeLeft / (1000 * 60 * 60)),
            minutes: Math.floor((timeLeft / 1000 / 60) % 60),
            seconds: Math.floor((timeLeft / 1000) % 60)
          }
        : defaultContext.timeLeftAsObject;

    const paddedHours = padWithZero(timeLeftAsObject.hours);
    const paddedMinutes = padWithZero(timeLeftAsObject.minutes);
    const paddedSeconds = padWithZero(timeLeftAsObject.seconds);

    const timeLeftAsString =
      timeLeftAsObject.hours > 0
        ? `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
        : `${paddedMinutes}:${paddedSeconds}`;

    const timeLeftAsShortString =
      timeLeftAsObject.hours > 0
        ? `${paddedHours}:${paddedMinutes}`
        : `${paddedMinutes}:${paddedSeconds}`;

    setTimeLeftState({
      timeLeft,
      timeLeftAsObject,
      timeLeftAsShortString,
      timeLeftAsString
    });
  };

export function UnlockTimerContextProvider({
  children
}: React.PropsWithChildren) {
  const { purchasePending } = useSelector(getAlertStatus);
  const { isLoaded } = useSelector(getUserAvailability);
  const availableDateTime = useSelector(getVehicleAvailableDateTime) || 0;
  const isInExclusivePreview = useSelector(getIsInExclusivePreview);

  const [isInitialized, setInitialized] = useState(false);
  const [timeLeft, setTimeLeft] = useState(defaultContext.timeLeft);
  const [timeLeftAsObject, setTimeLeftAsObject] = useState(
    defaultContext.timeLeftAsObject
  );
  const [timeLeftAsShortString, setTimeLeftAsShortString] = useState('00:00');
  const [timeLeftAsString, setTimeLeftAsString] = useState('00:00');
  const [wasTimerShown, setTimerWasShown] = useState(
    defaultContext.wasTimerShown
  );

  const setTimeLeftState = (values: TimeLeftObjectType) => {
    setInitialized(true);
    setTimeLeft(values.timeLeft);
    setTimeLeftAsObject(values.timeLeftAsObject);
    setTimeLeftAsShortString(values.timeLeftAsShortString);
    setTimeLeftAsString(values.timeLeftAsString);
  };

  useEffect(() => {
    if (!isLoaded) return () => {};

    if (!availableDateTime) {
      setInitialized(true);

      return () => {};
    }

    const calculateTimeLeftValues = calculateTimeLeft(
      availableDateTime,
      setTimeLeftState
    );

    calculateTimeLeftValues();

    const t = setInterval(calculateTimeLeftValues, 1000);

    return () => clearInterval(t);
  }, [availableDateTime, isLoaded]);

  const maxTime = purchasePending ? twoHours : fortyEightHours;
  const shouldShowTimer =
    isInitialized &&
    (purchasePending || isInExclusivePreview) &&
    Math.floor(timeLeft / 1000) < maxTime;

  useEffect(() => {
    if (shouldShowTimer) {
      setTimerWasShown(true);
    }
  }, [shouldShowTimer]);

  const providerValue = useMemo<ContextType>(
    (): ContextType => ({
      availableDateTime,
      isInitialized,
      shouldShowTimer,
      timeLeft,
      timeLeftAsObject,
      timeLeftAsShortString,
      timeLeftAsString,
      wasTimerShown
    }),
    [
      availableDateTime,
      isInitialized,
      shouldShowTimer,
      timeLeft,
      timeLeftAsObject,
      timeLeftAsShortString,
      timeLeftAsString,
      wasTimerShown
    ]
  );

  return (
    <UnlockTimerContext.Provider value={providerValue}>
      {children}
    </UnlockTimerContext.Provider>
  );
}

UnlockTimerContextProvider.displayName = 'UnlockTimerContextProvider';

export const generateUnlockTimerContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export default UnlockTimerContext;
